import { useState, useEffect } from "react";
import { db } from "@healthdesk/shared-services";
import firebase from "firebase/compat/app";
import {
  isValidMapCarouselSlideId,
  sortCpcFromListingGtgUsd,
  sortCpmFromListingGtgUsd,
  gtgMaxCpcUsdToChargeCents,
  gtgMaxCpmUsdToChargeCentsPerImpression,
  extractYoutubeVideoId,
  normalizeGtgTargetAudience,
  splitArrayIntoChunks,
} from "@healthdesk/shared-utils";
import useLocation from "./useLocation";

/** Must match CareMap `local_carousel` zip listing type (facility Local Carousel Ads). */
const LISTING_TYPE_LOCAL_CAROUSEL = "local_carousel";

const useZip = () => {
  const { zipCode: locationZip } = useLocation();
  const [zip, setZip] = useState();
  const [customLocation, setCustomLocation] = useState(null);
  const [allListings, setAllListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  /** Sorted like CareMap: CPC desc, then carousel listing order, then doc id. Symptom Checker strip reads this. */
  const [localCarouselListingRows, setLocalCarouselListingRows] = useState([]);
  const [listingsLoaded, setListingsLoaded] = useState(false);

  const getListings = async (zip) => {
    setListingsLoaded(false); // Reset loading state when starting new fetch

    try {
      const querySnapshot = await db
        .collection("zips")
        .doc(String(zip))
        .collection("listings")
        .where("status", "==", "active")
        .get();


      // Use Set to avoid duplicate location lookups
      const uniqueLocationIds = new Set();
      const localCarouselRows = [];
      const otherListingsData = [];
      const featuredListingsData = [];

      querySnapshot.forEach((docSnap) => {
        const listingData = docSnap.data();
        const { location, type } = listingData;

        if (type === LISTING_TYPE_LOCAL_CAROUSEL) {
          if (location) {
            uniqueLocationIds.add(location);
          }
          localCarouselRows.push({ listingData, docId: docSnap.id });
          return;
        }

        if (location) {
          uniqueLocationIds.add(location);
          otherListingsData.push(listingData);
          if (type === "featured") {
            featuredListingsData.push(listingData);
          }
        }
      });

      localCarouselRows.sort((a, b) => {
        const ca = Math.max(
          sortCpcFromListingGtgUsd(a.listingData.gtgMaxCpcUsd),
          sortCpmFromListingGtgUsd(a.listingData.gtgMaxCpmUsd)
        );
        const cb = Math.max(
          sortCpcFromListingGtgUsd(b.listingData.gtgMaxCpcUsd),
          sortCpmFromListingGtgUsd(b.listingData.gtgMaxCpmUsd)
        );
        if (cb !== ca) {
          return cb - ca;
        }
        const oa =
          typeof a.listingData.carouselListingOrder === "number"
            ? a.listingData.carouselListingOrder
            : 9999;
        const ob =
          typeof b.listingData.carouselListingOrder === "number"
            ? b.listingData.carouselListingOrder
            : 9999;
        if (oa !== ob) {
          return oa - ob;
        }
        return String(a.docId).localeCompare(String(b.docId));
      });

      setLocalCarouselListingRows(
        localCarouselRows.map((r) => ({
          listingData: r.listingData,
          docId: r.docId,
        }))
      );

      const allListingsData = [
        ...localCarouselRows.map((r) => ({
          ...r.listingData,
          _mapCarouselListingDocId: r.docId,
        })),
        ...otherListingsData,
      ];

      // Fetch unique locations using batch queries (Firestore allows up to 30 ids per `in` clause)
      const locationIds = Array.from(uniqueLocationIds);

      const locationChunks = splitArrayIntoChunks(locationIds);
      const batchQueries = locationChunks.map((chunk) =>
        db
          .collection("locations")
          .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
          .get()
      );

      const batchResults = await Promise.all(batchQueries);

      // Flatten results and create location lookup map
      const locationMap = {};
      batchResults.forEach((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          if (doc.exists) {
            locationMap[doc.id] = doc.data();
          }
        });
      });

      // Helper function to extract coordinates from location data
      const extractCoordinates = (locationData) => {
        if (locationData.coordinates) {
          // Handle Firestore GeoPoint
          if (
            locationData.coordinates.latitude &&
            locationData.coordinates.longitude
          ) {
            return {
              lat: locationData.coordinates.latitude,
              lng: locationData.coordinates.longitude,
            };
          }
          // Handle array format [lat, lng]
          if (
            Array.isArray(locationData.coordinates) &&
            locationData.coordinates.length === 2
          ) {
            return {
              lat: locationData.coordinates[0],
              lng: locationData.coordinates[1],
            };
          }
        }
        // Fallback to lat/lng fields
        if (locationData.lat && locationData.lng) {
          return {
            lat: locationData.lat,
            lng: locationData.lng,
          };
        }
        return null;
      };

      // Combine listing data with location data for all listings
      const combinedAllListings = allListingsData.flatMap((listing) => {
        if (
          listing.type === LISTING_TYPE_LOCAL_CAROUSEL &&
          Array.isArray(listing.localCarouselSlides)
        ) {
          const locationData = listing.location
            ? locationMap[listing.location]
            : null;
          const coordinates = locationData
            ? extractCoordinates(locationData)
            : null;
          const facilityType =
            locationData?.type === "Emergency Department"
              ? "emergency"
              : "clinic";
          const title =
            (locationData && locationData.group) ||
            listing.facilityName ||
            "Healthcare";
          const baseBranding =
            locationData && locationData.branding ? locationData.branding : {};

          return listing.localCarouselSlides
            .filter(
              (s) =>
                s &&
                s.enabled !== false &&
                String(s.message || "").trim().length > 0
            )
            .map((slide) => {
              const rawSlideId =
                typeof slide.id === "string" ? slide.id.trim() : "";
              const localCarouselSlideId = isValidMapCarouselSlideId(rawSlideId)
                ? rawSlideId
                : undefined;
              const docId =
                typeof listing._mapCarouselListingDocId === "string"
                  ? listing._mapCarouselListingDocId
                  : "";
              const linkToVideo = Boolean(slide.linkToVideo);
              const ytId = extractYoutubeVideoId(slide.href);
              const sortCpc = linkToVideo
                ? sortCpmFromListingGtgUsd(listing.gtgMaxCpmUsd)
                : sortCpcFromListingGtgUsd(listing.gtgMaxCpcUsd);
              return {
                title,
                ...baseBranding,
                tagline: slide.message,
                logo: slide.logoUrl || baseBranding.logo,
                type: LISTING_TYPE_LOCAL_CAROUSEL,
                patientGender:
                  slide.patientSegments?.gender || slide.patientGender || "all",
                coordinates,
                facilityType,
                website: slide.href || baseBranding.website,
                localCarouselButtonLabel: slide.buttonLabel || "Learn more",
                localCarouselSlideId,
                mapCarouselLinkToVideo: linkToVideo,
                mapCarouselYoutubeVideoId: ytId || undefined,
                mapCarouselLearnMoreHref:
                  typeof slide.learnMoreHref === "string" ? slide.learnMoreHref.trim() : "",
                gtgTargetAudience: normalizeGtgTargetAudience(
                  listing.gtgTargetAudience
                ),
                sortCpc,
                mapCarouselCpcChargeCents: linkToVideo
                  ? 0
                  : gtgMaxCpcUsdToChargeCents(listing.gtgMaxCpcUsd),
                mapCarouselCpmChargeCentsPerImpression: linkToVideo
                  ? gtgMaxCpmUsdToChargeCentsPerImpression(listing.gtgMaxCpmUsd)
                  : 0,
                mapCarouselListingDocId: docId,
                mapCarouselSourceZip: String(zip),
              };
            });
        }

        const locationData = locationMap[listing.location];
        if (locationData && locationData.branding && locationData.group) {
          const coordinates = extractCoordinates(locationData);
          const facilityType =
            locationData.type === "Emergency Department"
              ? "emergency"
              : "clinic";
          return [
            {
              title: locationData.group,
              ...locationData.branding,
              type: listing.type,
              coordinates,
              facilityType,
              sortCpc: 0,
            },
          ];
        }
        return [];
      });

      // Combine listing data with location data for featured listings
      const combinedFeaturedListings = featuredListingsData
        .map((listing) => {
          const locationData = locationMap[listing.location];
          if (locationData && locationData.branding && locationData.group) {
            const coordinates = extractCoordinates(locationData);

            // Determine facility type based on location data
            const facilityType =
              locationData.type === "Emergency Department"
                ? "emergency"
                : "clinic";

            return {
              title: locationData.group,
              ...locationData.branding,
              type: listing.type,
              coordinates,
              facilityType,
              sortCpc: 0,
            };
          }
          return null;
        })
        .filter(Boolean);

      setAllListings(combinedAllListings);
      setFeaturedListings(combinedFeaturedListings);
      setListingsLoaded(true);
    } catch (error) {
      const isPermissionDenied =
        error?.code === "permission-denied" ||
        (typeof error?.message === "string" &&
          error.message.toLowerCase().includes("permission"));
      if (isPermissionDenied) {
        console.debug(
          "useZip: Listings not readable (permission denied); using empty list."
        );
      } else {
        console.error("useZip: Error fetching listings:", error);
      }
      setAllListings([]);
      setFeaturedListings([]);
      setLocalCarouselListingRows([]);
      setListingsLoaded(true);
    }
  };

  useEffect(() => {
    const targetZip = customLocation?.zip || locationZip;

    if (targetZip) {
      setZip(targetZip);
      getListings(targetZip);
    } else {
      setListingsLoaded(true); // This might be our issue - we're setting loaded too early
      setLocalCarouselListingRows([]);
    }
  }, [locationZip, customLocation]);

  return {
    zip,
    setCustomLocation,
    listingsLoaded,
    localCarouselListingRows,
    allListings, // All active listings for carousel
    featuredListings, // Only featured listings for header
    // Legacy support
    ads: allListings,
    adsLoaded: listingsLoaded,
  };
};

export default useZip;
